import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { catchError, Subscription, throwError } from 'rxjs';
import { ChartComponent } from 'ng-apexcharts';
import { ChartConfigService } from 'src/app/core/services/utelize/general/chart-config-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';
import { WidgetSimNotInactiveTotalDTO } from 'src/app/core/models/system/widget/widget-sim-not-inactive-total.dto';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';

@Component({
  selector: 'app-chart-sim-not-inactive-per-day',
  templateUrl: './chart-sim-not-inactive-per-day.component.html',
  styleUrls: ['./chart-sim-not-inactive-per-day.component.scss'],
  providers: [TippyToolTipService]
})

export class ChartSimNotInactivePerDayComponent implements AfterViewInit, OnDestroy, OnInit {
  
  @Input() allowContainerClose: boolean | undefined;
  @Input() chartHeight        : number  | undefined;
  @Input() chartTitle         : string  | undefined;
  @Input() containerGrow      : boolean | undefined;
  @Input() evenDaysOnly       : boolean | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() showTitleHintText  : boolean | undefined;
  
  @Output() containerClosed         : EventEmitter<void>=new EventEmitter<void>();
  @Output() eSimManagerFormCancelled: EventEmitter<void>=new EventEmitter<void>();
  
  @ViewChild('chartRef') chartRef!: ChartComponent;
 
  private buildSimNotInactiveTotalChart   : boolean=true;
  private filterCompanyID                 : number       | undefined;
  private filterDate                      : OurDate      | undefined=populateOurDate(this.exceptionsService, Date.now());
  private subSimNotInactiveTotal!         : Subscription | undefined;
  private widgetSIMNotInactiveDayOfMonth  : string[]=[];
  private widgetSIMNotInactiveTotal       : number[]=[];
  
  public expandContainer     : boolean=true;
  public noData              : boolean=false;
  public simNotInactiveChart : any | undefined;
  public visibleContainer    : boolean=true;

  constructor(
    private chartConfigService : ChartConfigService,
    private exceptionsService  : ExceptionsService,
    private tippyToolTipService: TippyToolTipService,
    private widgetService      : WidgetService
  ) {

  }

  public ngOnInit(): void {
    try {
      if (this.chartHeight==undefined)
        this.chartHeight=250;

      if (this.chartTitle==undefined)
        this.chartTitle="SIM Not Inactive Per Day";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      this.buildSimNotInactiveTotalChart=true;
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnDestroy(): void {
    try {
      this.unsubscribeSimNotInactiveTotal();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getCompanySimNotInactiveTotal(filterCompanyID: number | undefined, filterDate: OurDate | undefined) {
    try {
      if (filterCompanyID==undefined)
        throw new Error("ChartSimNotInactivePerDayComponent - CompanyID is undefined");

      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate;

      this.getSimNotInactiveTotal();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
 
  public getSimNotInactiveTotal() {
    try {
      this.unsubscribeSimNotInactiveTotal();
      this.subSimNotInactiveTotal=new Subscription();

      const companyID  =<number>(this.filterCompanyID   ?? 0);
      const year       =<number>(this.filterDate?.year  ?? 1);
      const month      =<number>(this.filterDate?.month ?? 1);
      const day        =<number>(this.filterDate?.day   ?? 1);
      //CRN_20261013 - Retired
      // const sourceUsage=this.widgetService.getSimNotInactiveTotal(companyID, year, month, day);
      //
      // const sourceUsageSubscription=sourceUsage
      // .pipe(
      //   catchError(error =>
      //     throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
      //   )
      // )
      // .subscribe({
      //   next : data => this.updateSIMNotInactiveChart(data),
      //   error: error => this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)))
      // });
      //
      // this.subSimNotInactiveTotal.add(sourceUsageSubscription);

      const sourceUsageUpdates=this.widgetService.getSimNotInactiveTotalUpdates(companyID, year, month, day);

      const sourceUsageUpdatesSubscription=sourceUsageUpdates
      .pipe(
        catchError(error =>
          throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        )
      )
      .subscribe({
        next : data => this.updateSIMNotInactiveChart(data),
        error: error => this.exceptionsService.handleException(new Error(error))
      });

      this.subSimNotInactiveTotal.add(sourceUsageUpdatesSubscription);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private updateSIMNotInactiveChart(data: WidgetSimNotInactiveTotalDTO[]) {
    try {
      this.widgetSIMNotInactiveDayOfMonth  =[];
      this.widgetSIMNotInactiveTotal       =[];

      this.noData=(data ?? []).length===0;

      if (this.noData) {
        this.buildSimNotInactiveTotalChart=true;
        this.simNotInactiveChart          =undefined;
        return;
      }

      for (let d of data) {
        this.widgetSIMNotInactiveDayOfMonth.push(!this.evenDaysOnly || d.dayOfMonth%2==0 ? (<string><unknown>d.dayOfMonth) : "");
        this.widgetSIMNotInactiveTotal.push(d.total);
      }

      if (this.buildSimNotInactiveTotalChart) {
        this.buildSimNotInactiveTotalChart=false;
        
        //this.simNotInactiveTotalForCurrentMonthChart('["--vz-primary", "--vz-success", "--vz-danger"]');
        this.simNotInactiveTotalForCurrentMonthChart('["--vz-suresim-blue", "--vz-success", "--vz-danger"]');
      }

      if (this.chartRef?.updateSeries) {
        const series=[
          {
            name: 'Total',
            data: this.widgetSIMNotInactiveTotal,
            type: 'column'
          }
        ];

        this.chartRef.updateSeries(series, true);

        this.chartRef.updateOptions({
          chart: {
            zoom: { enabled: false },
            pan : { enabled: false }
          },
          labels     : this.widgetSIMNotInactiveDayOfMonth,
          plotOptions: this.buildPlotOptions(this.widgetSIMNotInactiveDayOfMonth.length),
          yaxis      : this.buildYAxis()
        }, false);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public unsubscribeSimNotInactiveTotal() {
    try {
      if (this.subSimNotInactiveTotal) {
        this.subSimNotInactiveTotal.unsubscribe();
        this.subSimNotInactiveTotal=undefined;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private simNotInactiveTotalForCurrentMonthChart(colors: any) {
    try {
      colors      =this.chartConfigService.resolveColours(colors);
      const series=[
        {
          name: 'Total',
          data: this.widgetSIMNotInactiveTotal,
          type: 'column'
        }
      ];

      const strokeWidths=[0]; //[0, 3];
      // const yaxis: any[]=[
      //   {
      //     title: {
      //       style: {
      //         fontWeight: 600,
      //       },
      //       text: 'Total'
      //     },
      //   }
      // ];

      this.simNotInactiveChart={
        chart: {
          height : this.chartHeight,
          pan: {
            enabled: false
          },
          toolbar: {
              show: false,
          },
          type: 'line',
          zoom: {
            enabled: false
          },
        },
        colors: colors,
        // dataLabels: {
        //   enabled        : true,
        //   enabledOnSeries: [1]
        // },
        labels: this.widgetSIMNotInactiveDayOfMonth,
        // plotOptions: {
        //   bar: {
        //     columnWidth: '90%'
        //   }
        // },
        plotOptions: this.buildPlotOptions(this.widgetSIMNotInactiveDayOfMonth.length),
        series     : series,
        stroke     : {
          curve: 'smooth',
          width: strokeWidths
        },
        title: {
          style: {
            fontWeight: 600,
          },
          text: undefined
        },
        xaxis: {
          type: 'string'
        },
        yaxis: this.buildYAxis()
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private buildPlotOptions(barCount: number): any {
    try {
      return {
        bar: {
          borderRadius           : 4,
          borderRadiusApplication: 'end',
          columnWidth            : barCount>12 ? '60%' : '40px'
        }
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { bar: { columnWidth: '60%' } };
    }
  }

  private buildYAxis(): any[] {
    try {
      const max=Math.max(0, ...this.widgetSIMNotInactiveTotal.map(v => Number(v) || 0));

      return [
        {
          forceNiceScale: true,
          labels        : { formatter: (value: number) => Math.round(value).toLocaleString() },
          max           : max>0 && max<=10 ? max : undefined,
          min           : 0,
          tickAmount    : max>0 && max<=10 ? max : undefined,
          title         : { style: { fontWeight: 600 }, text: 'Total' }
        }
      ];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [{ title: { text: 'Total' } }];
    }
  }
  
  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;

      if (this.expandContainer) {
        setTimeout(() => {
          try {
            window.dispatchEvent(new Event('resize'));
          }
          catch(e) {
            this.exceptionsService.handleException(<Error>e);
          }
        }, 0);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
